import { useState, useEffect } from 'react';
import { Crown, Lock, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function ExerciseList({ 
  exercises, 
  currentExercise, 
  onSelectExercise,
  completedIds = [],
  writtenInstructions = null
}) {
  const navigate = useNavigate();
  const [selectedTest, setSelectedTest] = useState(null);

  // Group exercises by sample_test if available
  const groupedExercises = exercises.reduce((groups, exercise) => {
    const testNum = exercise.sample_test || 0;
    if (!groups[testNum]) {
      groups[testNum] = [];
    }
    groups[testNum].push(exercise);
    return groups;
  }, {});

  const hasSampleTests = Object.keys(groupedExercises).some(key => key !== '0' && groupedExercises[key].length > 0);
  const sampleTestNumbers = Object.keys(groupedExercises)
    .filter(key => key !== '0')
    .map(Number)
    .sort((a, b) => a - b);

  // Set initial selected test based on current exercise
  useEffect(() => {
    if (currentExercise?.sample_test && hasSampleTests) {
      setSelectedTest(currentExercise.sample_test);
    } else if (hasSampleTests && sampleTestNumbers.length > 0 && !selectedTest) {
      setSelectedTest(sampleTestNumbers[0]);
    }
  }, [currentExercise, hasSampleTests, sampleTestNumbers]);

  if (!exercises || exercises.length <= 1) {
    return null;
  }

  const handleSelect = (exercise) => {
    if (exercise.is_locked) {
      return;
    }
    onSelectExercise(exercise);
  };

  const handleTestSelect = (testNum) => {
    if (isTestLocked(testNum)) {
      return;
    }
    setSelectedTest(testNum);
    // Auto-select first part of the test
    const testExercises = groupedExercises[testNum] || [];
    if (testExercises.length > 0 && !testExercises[0].is_locked) {
      onSelectExercise(testExercises[0]);
    }
  };

  const isTestLocked = (testNum) => {
    const testExercises = groupedExercises[testNum] || [];
    return testExercises.length > 0 && testExercises[0].is_locked;
  };

  const isTestFree = (testNum) => {
    const testExercises = groupedExercises[testNum] || [];
    return testExercises.length > 0 && testExercises[0].is_free === true;
  };

  const getTestCompletedCount = (testNum) => {
    const testExercises = groupedExercises[testNum] || [];
    return testExercises.filter(ex => completedIds.includes(ex.id)).length;
  };

  // Render two-level navigation for Sample Tests
  if (hasSampleTests) {
    const selectedTestExercises = selectedTest ? (groupedExercises[selectedTest] || []) : [];
    // Get instructions exercises (sample_test === 0 and is_instructions === true)
    const instructionsExercises = (groupedExercises[0] || []).filter(ex => ex.is_instructions);

    return (
      <div className="bg-white border border-slate-200 p-4 mb-6">
        {/* Instructions Section */}
        {(instructionsExercises.length > 0 || writtenInstructions) && (
          <div className="mb-4 pb-4 border-b border-slate-200">
            <h3 className="text-sm font-medium text-slate-500 mb-3">Instructions</h3>
            {instructionsExercises.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {instructionsExercises.map((exercise) => {
                  const isActive = currentExercise?.id === exercise.id;
                  return (
                    <button
                      key={exercise.id}
                      onClick={() => handleSelect(exercise)}
                      className={`
                        relative px-4 py-2.5 rounded-lg text-sm font-medium transition-all border
                        ${isActive 
                          ? 'bg-purple-600 text-white border-purple-600' 
                          : 'bg-purple-50 text-purple-700 border-purple-200 hover:border-purple-400 hover:bg-purple-100'
                        }
                      `}
                    >
                      <span className="flex items-center gap-2">
                        <span>🎧</span>
                        <span>Listen before starting the test</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {writtenInstructions && (
              <div className="text-sm text-slate-700 space-y-1">
                {writtenInstructions.map((line, i) => (
                  <p key={i} className={line === '' ? 'h-2' : ''}>{line}</p>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Level 1: Sample Tests */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-slate-500 mb-3">Select a Sample Test</h3>
          <div className="flex flex-wrap gap-2">
            {sampleTestNumbers.map((testNum) => {
              const isActive = selectedTest === testNum;
              const isLocked = isTestLocked(testNum);
              const isFree = isTestFree(testNum);
              const completedCount = getTestCompletedCount(testNum);
              const totalParts = (groupedExercises[testNum] || []).length;
              const isCompleted = completedCount === totalParts && totalParts > 0;

              return (
                <button
                  key={testNum}
                  onClick={() => handleTestSelect(testNum)}
                  disabled={isLocked}
                  className={`
                    relative px-4 py-2.5 rounded-lg text-sm font-medium transition-all border
                    ${isActive 
                      ? 'bg-slate-900 text-white border-slate-900' 
                      : isLocked
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                    }
                  `}
                >
                  <div className="flex flex-col items-start gap-1">
                    <span className="flex items-center gap-2">
                      {isLocked && <Lock className="w-4 h-4" />}
                      {isCompleted && !isLocked && <Check className="w-4 h-4 text-green-500" />}
                      <span>Sample Test {testNum}</span>
                      {isFree && !isActive && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs ml-1">
                          FREE
                        </span>
                      )}
                      {!isFree && !isLocked && !isActive && (
                        <Crown className="w-3.5 h-3.5 text-amber-500" />
                      )}
                    </span>
                    {/* Progress indicator */}
                    {!isLocked && (
                      <span className={`text-xs ${isActive ? 'text-slate-300' : 'text-slate-400'}`}>
                        {completedCount}/{totalParts} completed
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Level 2: Parts of selected test */}
        {selectedTest && selectedTestExercises.length > 0 && (
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-medium text-slate-500">Sample Test {selectedTest}</h3>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Select a Part</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedTestExercises.map((exercise) => {
                const isActive = currentExercise?.id === exercise.id;
                const isCompleted = completedIds.includes(exercise.id);
                const isLocked = exercise.is_locked;

                return (
                  <button
                    key={exercise.id}
                    onClick={() => handleSelect(exercise)}
                    disabled={isLocked}
                    className={`
                      relative px-4 py-2 rounded-lg text-sm font-medium transition-all border
                      ${isActive 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : isLocked
                          ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                      }
                    `}
                    title={exercise.title_es || exercise.title}
                  >
                    <span className="flex items-center gap-1.5">
                      {isLocked && <Lock className="w-3.5 h-3.5" />}
                      {isCompleted && !isLocked && <Check className="w-3.5 h-3.5 text-green-500" />}
                      Part {exercise.exam_part || 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Premium Upsell */}
        {exercises.some(e => e.is_locked) && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <Crown className="w-4 h-4 text-amber-500" />
              <span>Unlock all Sample Tests with Premium</span>
            </div>
            <Button
              onClick={() => navigate('/subscription')}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-white rounded-full"
            >
              Get Premium
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Original display for non-Sample Test exercises (B1, C1, C2, etc.)
  return (
    <div className="bg-white border border-slate-200 p-4 mb-6">
      <h3 className="text-sm font-medium text-slate-700 mb-3">Available Exercises</h3>
      <div className="flex flex-wrap gap-2">
        {exercises.map((exercise, index) => {
          const isActive = currentExercise?.id === exercise.id;
          const isCompleted = completedIds.includes(exercise.id);
          const isLocked = exercise.is_locked;

          return (
            <button
              key={exercise.id}
              onClick={() => handleSelect(exercise)}
              disabled={isLocked}
              className={`
                relative px-4 py-2 rounded-lg text-sm font-medium transition-all border
                ${isActive 
                  ? 'bg-slate-900 text-white border-slate-900' 
                  : isLocked
                    ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                }
              `}
            >
              <span className="flex items-center gap-2">
                {isLocked && <Lock className="w-3.5 h-3.5" />}
                {isCompleted && !isLocked && <Check className="w-3.5 h-3.5 text-green-500" />}
                Exercise {index + 1}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Premium Upsell */}
      {exercises.some(e => e.is_locked) && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-700">
            <Crown className="w-4 h-4 text-amber-500" />
            <span>Unlock all exercises with Premium</span>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            size="sm"
            className="bg-amber-500 hover:bg-amber-400 text-white rounded-full"
          >
            Get Premium
          </Button>
        </div>
      )}
    </div>
  );
}
